import React, { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

export default function MobileSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select",
  className,
  disabled,
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const selectedLabel = options.find((o) => o.value === value)?.label;

  if (!isMobile) {
    return (
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger className={cn("rounded-xl", className, disabled && "opacity-60 pointer-events-none")}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal rounded-xl bg-transparent",
            className,
            disabled && "opacity-60 pointer-events-none"
          )}
        >
          {selectedLabel || (
            <span className="text-slate-400 dark:text-slate-500">{placeholder}</span>
          )}
          <ChevronDown className="w-4 h-4 opacity-50" />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[calc(100dvh-env(safe-area-inset-top))] pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <DrawerHeader className="text-left">
          <DrawerTitle>{placeholder}</DrawerTitle>
        </DrawerHeader>
        <div className="px-3 pb-6 max-h-[60vh] overflow-y-auto space-y-1">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onValueChange?.(o.value);
                setOpen(false);
              }}
              className={cn(
                "w-full text-left px-3 py-3 rounded-xl text-sm transition-colors",
                o.value === value
                  ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 font-medium"
                  : "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  );
}